import React from 'react';
import { 
  Brain, 
  Cloud, 
  BarChart3, 
  Cog, 
  ShieldCheck, 
  UserCheck,
  Code2,
  Database,
  Layers,
  Terminal,
  Building2,
  Users2,
  Sparkles
} from 'lucide-react';
import Banner from "../../assets/Banner4.png";

export default function HeroBanner() {
  // Left feature cards (Updated with CRM)
  const featuresLeft = [
    {
      title: 'CRM',
      subtitle: 'Platforms',
      icon: <Users2 className="w-5 h-5 text-[#f25c05]" />,
      aos: 'fade-right',
      delay: '50',
      highlight: true
    },
    {
      title: 'AI & ML',
      subtitle: 'Solutions',
      icon: <Brain className="w-5 h-5 text-[#f25c05]" />,
      aos: 'fade-right',
      delay: '150',
    },
    {
      title: 'Cloud',
      subtitle: 'Services',
      icon: <Cloud className="w-5 h-5 text-[#f25c05]" />,
      aos: 'fade-right',
      delay: '250',
    },
    {
      title: 'Data',
      subtitle: 'Analytics',
      icon: <BarChart3 className="w-5 h-5 text-[#f25c05]" />,
      aos: 'fade-right',
      delay: '350',
    },
  ];

  // Right feature cards (Updated with ERP)
  const featuresRight = [
    {
      title: 'ERP',
      subtitle: 'Systems',
      icon: <Building2 className="w-5 h-5 text-[#f25c05]" />,
      aos: 'fade-left',
      delay: '50',
      highlight: true
    },
    {
      title: 'Automation',
      subtitle: '& Integration',
      icon: <Cog className="w-5 h-5 text-[#f25c05]" />,
      aos: 'fade-left',
      delay: '150',
    },
    {
      title: 'Cyber',
      subtitle: 'Security',
      icon: <ShieldCheck className="w-5 h-5 text-[#f25c05]" />,
      aos: 'fade-left',
      delay: '250',
    },
    {
      title: 'Consulting',
      subtitle: 'Services',
      icon: <UserCheck className="w-5 h-5 text-[#f25c05]" />,
      aos: 'fade-left',
      delay: '350',
    },
  ];

  // Full-Stack & Enterprise Technology Badges
  const techPills = [
    { label: 'CRM Solutions', icon: <Users2 className="w-3.5 h-3.5 text-[#f25c05]" /> },
    { label: 'ERP Systems', icon: <Building2 className="w-3.5 h-3.5 text-[#f25c05]" /> },
    { label: 'MERN & MEAN', icon: <Database className="w-3.5 h-3.5 text-[#f25c05]" /> },
    { label: 'AI Integration', icon: <Brain className="w-3.5 h-3.5 text-[#f25c05]" /> },
    { label: 'Full-Stack Web', icon: <Terminal className="w-3.5 h-3.5 text-[#f25c05]" /> },
  ];

  const marqueeRow1 = ['CUSTOM CRM', 'ERP SYSTEMS', 'ENTERPRISE AI', 'HTML5', 'CSS3', 'MERN STACK', 'MEAN STACK', 'REACT.JS', 'NODE.JS'];
  const marqueeRow2 = ['DATABASE DESIGN', 'WORKFLOW AUTOMATION', 'REST APIs', 'JAVASCRIPT', 'TYPESCRIPT', 'TAILWIND CSS', 'CLOUD INFRA'];

  const allFeatures = [...featuresLeft, ...featuresRight];

  return (
    <section className="relative w-full bg-gradient-to-br from-[#f8fafc] via-[#f1f5f9] to-[#e2e8f0] py-12 lg:py-16 font-sans text-slate-800 overflow-hidden min-h-[85vh] flex items-center hidden md:block">
      
      {/* ================= INLINE KEYFRAME ANIMATIONS FOR BG TICKER ================= */}
      <style>{`
        @keyframes marquee-left {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0%); }
        }
        .animate-marquee-left {
          display: flex;
          width: 200%;
          animation: marquee-left 25s linear infinite;
        }
        .animate-marquee-right {
          display: flex;
          width: 200%;
          animation: marquee-right 28s linear infinite;
        }
      `}</style>

      {/* ================= ANIMATED MOVING BG TECH TEXT STREAM ================= */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden select-none opacity-[0.06] flex flex-col justify-around py-10 z-0">
        
        {/* Moving Belt 1 */}
        <div className="animate-marquee-left flex whitespace-nowrap">
          <div className="flex gap-12 text-6xl sm:text-8xl font-black text-slate-900 tracking-tighter">
            {[...marqueeRow1, ...marqueeRow1].map((tech, idx) => (
              <span key={idx} className="flex items-center gap-6">
                <span>{tech}</span>
                <span className="text-[#f25c05]">•</span>
              </span>
            ))}
          </div>
        </div>

        {/* Moving Belt 2 */}
        <div className="animate-marquee-right flex whitespace-nowrap">
          <div className="flex gap-12 text-6xl sm:text-8xl font-black text-slate-900 tracking-tighter">
            {[...marqueeRow2, ...marqueeRow2].map((tech, idx) => (
              <span key={idx} className="flex items-center gap-6">
                <span>{tech}</span>
                <span className="text-[#f25c05]">•</span>
              </span>
            ))}
          </div>
        </div>

      </div>

      {/* Soft Ambient Radial Blur Glows */}
      <div className="absolute -top-24 -left-24 w-80 h-80 bg-orange-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-slate-300/50 rounded-full blur-3xl pointer-events-none" />

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* ================= LEFT COLUMN: HERO TEXT ================= */}
          <div className="lg:col-span-7 space-y-6 text-left max-w-2xl mx-auto lg:mx-0">
            
            {/* Tagline Badge */}
            <div data-aos="fade-down" data-aos-delay="100">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-100/90 border border-orange-200 text-xs sm:text-sm font-extrabold tracking-[0.18em] text-[#f25c05] uppercase shadow-sm">
                <Sparkles className="w-3.5 h-3.5" />
                <span>INNOVATE. INTEGRATE. ELEVATE.</span>
              </span>
            </div>

            {/* Main Headline */}
            <h1 
              data-aos="fade-up" 
              data-aos-delay="200"
              className="text-4xl sm:text-5xl lg:text-[56px] xl:text-[62px] font-black text-slate-900 leading-[1.05] tracking-[-0.03em]"
            >
              Enterprise CRM, ERP &
              <br />
              <span className="text-[#f25c05] block mt-1">AI-Driven Solutions</span>
            </h1>

            {/* Description */}
            <p 
              data-aos="fade-up" 
              data-aos-delay="300"
              className="text-slate-600 text-base sm:text-lg lg:text-[19px] leading-relaxed max-w-xl font-normal tracking-normal"
            >
              Inimai Solutions empowers growing enterprises with modern software engineering—specializing in bespoke <strong className="font-bold text-slate-900">CRM & ERP platforms</strong>, high-performance <strong className="font-bold text-slate-900">MERN & MEAN web apps</strong>, and intelligent <strong className="font-bold text-slate-900">AI workflow integration</strong>.
            </p>

            {/* Tech & Solution Badges */}
            <div 
              data-aos="fade-up" 
              data-aos-delay="350"
              className="flex flex-wrap gap-2 pt-1"
            >
              {techPills.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm border border-orange-200/80 px-3 py-1.5 rounded-lg shadow-sm text-xs font-bold text-slate-700 hover:border-[#f25c05] hover:text-[#f25c05] transition-all"
                >
                  {item.icon}
                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div 
              data-aos="fade-up" 
              data-aos-delay="400"
              className="flex flex-wrap items-center gap-4 pt-2"
            >
              <a
                href="#services"
                className="group inline-flex items-center gap-2 bg-[#f25c05] hover:bg-[#d95000] text-white font-semibold text-sm px-6 py-3.5 rounded-xl shadow-lg shadow-[#f25c05]/30 transition-all duration-300 hover:scale-[1.02]"
              >
                <span>Explore Services</span>
                <svg className="w-4 h-4 stroke-current stroke-[2.5] transform transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </a>

              <a
                href="/about"
                className="group inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-slate-800 border-2 border-orange-200 hover:border-[#f25c05] font-semibold text-sm px-6 py-3 rounded-xl shadow-sm transition-all duration-300 hover:scale-[1.02]"
              >
                <span>About Us</span>
                <svg className="w-4 h-4 text-[#f25c05] stroke-current stroke-[2.5] transform transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </div>

          </div>

          {/* ================= RIGHT COLUMN: 3D BANNER IMAGE & FLOATING CARDS ================= */}
          <div className="lg:col-span-5 relative flex items-center justify-center min-h-[460px] sm:min-h-[500px] lg:min-h-[540px] w-full mt-6 lg:mt-0">
            
            {/* 3D Floating Banner Image Container */}
            <div 
              data-aos="zoom-in" 
              data-aos-delay="200"
              className="relative z-10 w-full max-w-[280px] sm:max-w-[360px] md:max-w-[400px] lg:max-w-[420px] flex justify-center items-center"
            >
              <div className="relative w-full flex items-center justify-center animate-bounce [animation-duration:6s]">
                
                {/* 3D Tilted Image Wrapper */}
                <div className="relative transform rotate-x-[12deg] rotate-y-[-12deg] rotate-z-[2deg] hover:rotate-x-0 hover:rotate-y-0 hover:rotate-z-0 transition-all duration-700 ease-out rounded-2xl overflow-hidden border border-white/80 bg-white/50 backdrop-blur-sm shadow-2xl shadow-slate-300/50">
                  <img 
                    src={Banner} 
                    alt="Inimai Solutions Enterprise Services" 
                    className="w-full h-auto object-contain max-h-[340px] sm:max-h-[380px]"
                  />
                </div>

              </div>
            </div>

            {/* Desktop Floating Cards */}
            <div className="absolute inset-0 w-full h-full pointer-events-none hidden md:block z-20">
              
              {/* Left Floating Cards */}
              <div 
                data-aos={featuresLeft[0].aos} 
                data-aos-delay={featuresLeft[0].delay}
                className="absolute top-[-2%] left-[-10%] xl:left-[-6%] pointer-events-auto transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-2.5 bg-gradient-to-r from-orange-500 to-[#f25c05] text-white px-3.5 py-2.5 rounded-xl border border-orange-400 shadow-xl shadow-orange-500/20">
                  <p className="text-xs font-black text-right leading-tight">
                    {featuresLeft[0].title}<br />
                    <span className="font-medium text-orange-100 text-[10px]">{featuresLeft[0].subtitle}</span>
                  </p>
                  <div className="p-2 bg-white/20 backdrop-blur-md rounded-lg flex items-center justify-center text-white">
                    <Users2 className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>

              <div 
                data-aos={featuresLeft[1].aos} 
                data-aos-delay={featuresLeft[1].delay}
                className="absolute top-[28%] left-[-14%] xl:left-[-10%] pointer-events-auto transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-2.5 bg-white/95 backdrop-blur-md px-3.5 py-2.5 rounded-xl border border-slate-200/80 shadow-lg shadow-slate-200/50">
                  <p className="text-xs font-bold text-slate-800 text-right leading-tight">
                    {featuresLeft[1].title}<br />
                    <span className="font-medium text-slate-500 text-[10px]">{featuresLeft[1].subtitle}</span>
                  </p>
                  <div className="p-2 bg-orange-50 border border-orange-200/60 rounded-lg flex items-center justify-center">
                    {featuresLeft[1].icon}
                  </div>
                </div>
              </div>

              <div 
                data-aos={featuresLeft[2].aos} 
                data-aos-delay={featuresLeft[2].delay}
                className="absolute top-[58%] left-[-14%] xl:left-[-10%] pointer-events-auto transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-2.5 bg-white/95 backdrop-blur-md px-3.5 py-2.5 rounded-xl border border-slate-200/80 shadow-lg shadow-slate-200/50">
                  <p className="text-xs font-bold text-slate-800 text-right leading-tight">
                    {featuresLeft[2].title}<br />
                    <span className="font-medium text-slate-500 text-[10px]">{featuresLeft[2].subtitle}</span>
                  </p>
                  <div className="p-2 bg-orange-50 border border-orange-200/60 rounded-lg flex items-center justify-center">
                    {featuresLeft[2].icon}
                  </div>
                </div>
              </div>

              <div 
                data-aos={featuresLeft[3].aos} 
                data-aos-delay={featuresLeft[3].delay}
                className="absolute bottom-[-2%] left-[-10%] xl:left-[-6%] pointer-events-auto transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-2.5 bg-white/95 backdrop-blur-md px-3.5 py-2.5 rounded-xl border border-slate-200/80 shadow-lg shadow-slate-200/50">
                  <p className="text-xs font-bold text-slate-800 text-right leading-tight">
                    {featuresLeft[3].title}<br />
                    <span className="font-medium text-slate-500 text-[10px]">{featuresLeft[3].subtitle}</span>
                  </p>
                  <div className="p-2 bg-orange-50 border border-orange-200/60 rounded-lg flex items-center justify-center">
                    {featuresLeft[3].icon}
                  </div>
                </div>
              </div>

              {/* Right Floating Cards */}
              <div 
                data-aos={featuresRight[0].aos} 
                data-aos-delay={featuresRight[0].delay}
                className="absolute top-[-2%] right-[-10%] xl:right-[-6%] pointer-events-auto transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-2.5 bg-gradient-to-r from-[#f25c05] to-orange-500 text-white px-3.5 py-2.5 rounded-xl border border-orange-400 shadow-xl shadow-orange-500/20">
                  <div className="p-2 bg-white/20 backdrop-blur-md rounded-lg flex items-center justify-center text-white">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-xs font-black text-left leading-tight">
                    {featuresRight[0].title}<br />
                    <span className="font-medium text-orange-100 text-[10px]">{featuresRight[0].subtitle}</span>
                  </p>
                </div>
              </div>

              <div 
                data-aos={featuresRight[1].aos} 
                data-aos-delay={featuresRight[1].delay}
                className="absolute top-[28%] right-[-14%] xl:right-[-10%] pointer-events-auto transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-2.5 bg-white/95 backdrop-blur-md px-3.5 py-2.5 rounded-xl border border-slate-200/80 shadow-lg shadow-slate-200/50">
                  <div className="p-2 bg-orange-50 border border-orange-200/60 rounded-lg flex items-center justify-center">
                    {featuresRight[1].icon}
                  </div>
                  <p className="text-xs font-bold text-slate-800 text-left leading-tight">
                    {featuresRight[1].title}<br />
                    <span className="font-medium text-slate-500 text-[10px]">{featuresRight[1].subtitle}</span>
                  </p>
                </div>
              </div>

              <div 
                data-aos={featuresRight[2].aos} 
                data-aos-delay={featuresRight[2].delay}
                className="absolute top-[58%] right-[-14%] xl:right-[-10%] pointer-events-auto transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-2.5 bg-white/95 backdrop-blur-md px-3.5 py-2.5 rounded-xl border border-slate-200/80 shadow-lg shadow-slate-200/50">
                  <div className="p-2 bg-orange-50 border border-orange-200/60 rounded-lg flex items-center justify-center">
                    {featuresRight[2].icon}
                  </div>
                  <p className="text-xs font-bold text-slate-800 text-left leading-tight">
                    {featuresRight[2].title}<br />
                    <span className="font-medium text-slate-500 text-[10px]">{featuresRight[2].subtitle}</span>
                  </p>
                </div>
              </div>

              <div 
                data-aos={featuresRight[3].aos} 
                data-aos-delay={featuresRight[3].delay}
                className="absolute bottom-[-2%] right-[-10%] xl:right-[-6%] pointer-events-auto transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-2.5 bg-white/95 backdrop-blur-md px-3.5 py-2.5 rounded-xl border border-slate-200/80 shadow-lg shadow-slate-200/50">
                  <div className="p-2 bg-orange-50 border border-orange-200/60 rounded-lg flex items-center justify-center">
                    {featuresRight[3].icon}
                  </div>
                  <p className="text-xs font-bold text-slate-800 text-left leading-tight">
                    {featuresRight[3].title}<br />
                    <span className="font-medium text-slate-500 text-[10px]">{featuresRight[3].subtitle}</span>
                  </p>
                </div>
              </div>

            </div>

            {/* Mobile Card Grid (<768px) */}
            <div className="grid grid-cols-2 gap-3 mt-6 w-full md:hidden relative z-30 px-1">
              {allFeatures.map((item, idx) => (
                <div 
                  key={idx} 
                  data-aos="fade-up"
                  data-aos-delay={idx * 50}
                  className={`flex items-center gap-2.5 p-3 rounded-xl border shadow-sm ${
                    item.highlight 
                      ? 'bg-[#f25c05] border-orange-400 text-white' 
                      : 'bg-white/90 backdrop-blur-sm border-slate-200 text-slate-800'
                  }`}
                >
                  <div className={`p-2 rounded-lg shrink-0 flex items-center justify-center ${
                    item.highlight ? 'bg-white/20 text-white' : 'bg-orange-50 border border-orange-200/60'
                  }`}>
                    {item.icon}
                  </div>
                  <p className="text-xs font-bold leading-tight">
                    {item.title}<br />
                    <span className={`text-[10px] ${item.highlight ? 'text-orange-100 font-normal' : 'text-slate-500 font-normal'}`}>
                      {item.subtitle}
                    </span>
                  </p>
                </div>
              ))}
            </div>

          </div>

        </div>
      </div>

    </section>
  );
}